import { HTMLAttributes, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Typography from '@/components/base/Typography'
import { useLenis } from '@/contexts/lenis'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPPageData } from '@/types'

export type ScrollImgProps = HTMLAttributes<HTMLElement> &
  Partial<{
    items: {
      image: WPImage
      label: string
      link: WPLink
    }[]
    theme: WPPageData['acf']['theme']
  }>

const ScrollImg = ({
  items,
  theme = 'light',
  className,
  ...props
}: ScrollImgProps) => {
  const wrapper = useRef<HTMLDivElement>(null!)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const { lenis } = useLenis()

  useGSAP(
    (context) => {
      if (!lenis || !loaded) return
      const selector = context?.selector || gsap.utils.selector(wrapper)
      const items = selector('[data-motion-scroll-img]')

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper.current,
          invalidateOnRefresh: true,
          scrub: 0.5,
          start: 'top 90%',
          end: 'top 20%'
        }
      })

      items.forEach((item: HTMLElement, index: number) => {
        timeline.fromTo(
          item,
          {
            y: 60 * index
          },
          {
            y: 0,
            duration: 4,
            ease: 'linear'
          },
          '<'
        )
      })
      return () => {
        timeline.kill()
      }
    },
    {
      dependencies: [lenis, loaded],
      scope: wrapper,
      revertOnUpdate: true
    }
  )

  return items && items?.length > 0 ? (
    <section
      ref={wrapper}
      {...props}
      className={cn([
        'z-30',
        {
          'bg-dark-100': theme === 'light',
          'bg-dark-600': theme === 'dark'
        },
        className
      ])}
    >
      <Container>
        <Grid className="gap-y-[11.2rem]">
          {items?.map((item, index) => (
            <div
              key={index}
              className={cn(['col-span-4', 'md:col-span-3', 'lg:col-span-3'])}
              data-motion-scroll-img
            >
              {item.link ? (
                <Link href={item.link.url} target={item.link.target}>
                  {item.image ? (
                    <div className="aspect-h-4 aspect-w-3">
                      <Image source={item.image} size="large" fit="cover" />
                    </div>
                  ) : null}
                  {item.label ? (
                    <Typography variant="label" className="font-bold">
                      {item.label}
                    </Typography>
                  ) : null}
                </Link>
              ) : (
                <>
                  {item.image ? (
                    <div className="aspect-h-4 aspect-w-3">
                      <Image source={item.image} size="large" fit="cover" />
                    </div>
                  ) : null}
                  {item.label ? (
                    <Typography variant="label" className="font-bold">
                      {item.label}
                    </Typography>
                  ) : null}
                </>
              )}
            </div>
          ))}
        </Grid>
      </Container>
    </section>
  ) : null
}

export default ScrollImg
